'use client';

import React from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { css } from 'styled-system/css';
import { Divider } from 'styled-system/jsx';

import ShaojiangLink from '@shaojiang/components/ShaojiangLink';
import Tooltip from '@shaojiang/components/Tooltip';
import { Drawer } from '@shaojiang/components/ui/drawer';
import { IconButton } from '@shaojiang/components/ui/icon-button';
import { useSidebar } from '@shaojiang/hooks/useSidebar';
import { useDomStore } from '@shaojiang/store/dom';
import { PropsWithI18nMessages } from '@shaojiang/types/common';

import Menu from './Menu';
import menus from './menus';

export default function Sidebar({ i18nMessages }: PropsWithI18nMessages) {
  const mobileSidebarOpen = useDomStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useDomStore((state) => state.setMobileSidebarOpen);
  const { sidebarWidth, toggleShowMini, showMini, xlDown, mdDown } = useSidebar();

  const messages = i18nMessages?.menus || {};
  const i18nMenus = menus.map((menu) => ({
    ...menu,
    title: messages[menu.i18nKey] || menu.title,
    subMenus: (menu.subMenus || []).map((subMenu) => ({
      ...subMenu,
      title: messages[subMenu.i18nKey] || subMenu.title,
    })),
  }));

  const list = (
    <aside
      style={{ width: sidebarWidth }}
      className={css({
        height: 'full',
        flexShrink: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        pr: '2',
        bg: 'bg.surface',
        boxShadow: 'xl',
        transition: 'width 0.2s ease',
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: showMini ? 'center' : 'space-between',
          minH: 'header',
          px: showMini ? '0' : '4',
          gap: '1',
          flexDirection: showMini ? 'column' : 'row',
          py: showMini ? '2' : '0',
        })}
      >
        <ShaojiangLink href='/' aria-label='Home'>
          <Image src='/avatar.png' alt='Shaojiang' width={48} height={48} priority />
        </ShaojiangLink>
        {!xlDown && (
          <Tooltip title='Toggle Mini Sidebar' placement={showMini ? 'right' : 'bottom'}>
            <IconButton
              variant='ghost'
              colorPalette='gray'
              size='sm'
              aria-label='Toggle mini sidebar'
              onClick={toggleShowMini}
            >
              {showMini ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Divider color='border.subtle' />
      <nav>
        <ul className={css({ py: '2' })}>
          {i18nMenus.map((menu) => (
            <Menu menu={menu} key={menu.href} showMini={showMini} />
          ))}
        </ul>
      </nav>
    </aside>
  );

  if (mdDown) {
    return (
      <Drawer.Root
        variant='left'
        open={mobileSidebarOpen}
        onOpenChange={({ open }) => setMobileSidebarOpen(open)}
      >
        <Drawer.Backdrop />
        {/* Park's positioner is full-width and centers its content; pin the panel to the left edge */}
        <Drawer.Positioner justifyContent='flex-start' width='auto'>
          <Drawer.Content
            width='auto'
            height='100dvh'
            display='flex'
            bg='bg.surface'
            p='0'
            className={css({ '& > aside': { boxShadow: 'none', h: 'full' } })}
          >
            {list}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    );
  }

  return list;
}
